import { useCallback } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import {
  IncidentDocument,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import { IncidentActionType } from '@/graphql/generated/schemas'
import { TOGGLE, mixpanel } from '@/utils/analytics'

import { MixpanelDataIProps } from '../types/types'

const MIXPANEL_TAGS = {
  REAL: 'Real',
  FALSE: 'False',
} as const

export interface UseSetIsIncidentRealReturnIProps {
  setIncidentReal: (
    incidentId: string,
    isReal: boolean,
    showToasts: boolean,
    mixpanelData?: MixpanelDataIProps
  ) => Promise<void>
  isLoading: boolean
}

export const useSetIsIncidentReal = (): UseSetIsIncidentRealReturnIProps => {
  const [createIncidentAction, { loading: isLoading }] =
    useCreateIncidentActionMutation()
  const { showSuccess, showError } = useToasts({
    preventDuplicate: true,
    defaultErrorMessage: 'Failed to update incident.',
  })

  const setIncidentReal = useCallback(
    async (
      incidentId: string,
      isReal: boolean,
      showToasts: boolean,
      mixpanelData?: MixpanelDataIProps
    ) => {
      try {
        const realValue = isReal
          ? IncidentActionType.SetRealToTrue
          : IncidentActionType.SetRealToFalse
        await createIncidentAction({
          variables: {
            input: {
              incidentId,
              type: realValue,
            },
          },
          refetchQueries: [
            { query: IncidentDocument, variables: { id: incidentId } },
          ],
          awaitRefetchQueries: true,
        })
        const toggleTag = isReal ? MIXPANEL_TAGS.REAL : MIXPANEL_TAGS.FALSE
        mixpanel.track(`${TOGGLE} Mark Incident ${toggleTag}`, mixpanelData)
        if (showToasts) {
          showSuccess('Incident updated.')
        }
      } catch (error) {
        Sentry.captureException(error)
        if (showToasts) {
          showError()
        }
      }
    },
    [createIncidentAction, showSuccess, showError]
  )
  return { setIncidentReal, isLoading }
}
